'use client';

import { useMemo } from 'react';
import { ArrowDownIcon, ArrowUpIcon } from '@phosphor-icons/react';
import { fmt } from '@/lib/transactions/utils';
import { cn } from '@/lib/utils';
import { MoneyValue } from '@/components/ui/MoneyValue';

interface Props {
  variant: 'income' | 'expense' | 'net';
  amount: number;
  /** Number of transactions for this variant (income/expense). */
  count?: number;
  /** Per-bucket values across the period, ordered oldest → newest. */
  series?: readonly number[];
  /** Total days in the active period — drives the per-day secondary line. */
  periodDays?: number;
  /** For the Net card: the income total, used to derive savings rate. */
  incomeForRate?: number;
  /** Extra classes on the card root — e.g. grid column-span so the lone net card fills a wrapped row. */
  className?: string;
}

const CONFIG = {
  income: {
    label: 'Entradas',
    Icon: ArrowUpIcon,
    accentClass: 'text-amount-positive',
    dotClass: 'bg-amount-positive',
    sparkColor: '#298D5D',
  },
  expense: {
    label: 'Saídas',
    Icon: ArrowDownIcon,
    accentClass: 'text-amount-negative',
    dotClass: 'bg-amount-negative',
    sparkColor: '#BD534A',
  },
  net: {
    label: 'Líquido',
    Icon: null,
    accentClass: 'text-text-primary',
    dotClass: 'bg-text-primary',
    sparkColor: '#1F2937',
  },
} as const;

const perDayFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  notation: 'compact',
  maximumFractionDigits: 0,
});

function Sparkline({
  values,
  color,
  gradientId,
}: {
  values: readonly number[];
  color: string;
  gradientId: string;
}) {
  const { line, area } = useMemo(() => {
    if (values.length < 2) return { line: null as string | null, area: '' };
    const min = Math.min(...values, 0);
    const max = Math.max(...values, 0);
    const range = max - min || 1;
    const w = 100;
    const h = 36;
    const step = w / (values.length - 1);
    let line = '';
    values.forEach((v, i) => {
      const x = i * step;
      const y = h - ((v - min) / range) * h;
      line += `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)} `;
    });
    line = line.trim();
    // Close the line down to the baseline and back to form the filled area.
    return { line, area: `${line} L ${w} ${h} L 0 ${h} Z` };
  }, [values]);

  if (!line) return <div className="h-9" aria-hidden />;

  return (
    <svg
      viewBox="0 0 100 36"
      preserveAspectRatio="none"
      className="h-9 w-full"
      aria-hidden
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.2} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradientId})`} stroke="none" />
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth={1.75}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

export function TotalCard({
  variant,
  amount,
  count,
  series,
  periodDays,
  incomeForRate,
  className,
}: Props) {
  const cfg = CONFIG[variant];
  const Icon = cfg.Icon;
  // Show the magnitude; the arrow (income ↑ / expense ↓) carries direction, matching the concept
  // — no redundant minus on the expense card. Net keeps its sign (it can be a surplus or a deficit).
  const displayed = variant === 'net' ? amount : Math.abs(amount);
  const accent =
    variant === 'net'
      ? amount < 0
        ? 'text-amount-negative'
        : amount > 0
          ? 'text-amount-positive'
          : 'text-text-primary'
      : cfg.accentClass;

  // Area fill is semantic: green for income, red for expense, and dynamic for the net card (green
  // when the balance is positive, red when negative). References the v2 amount tokens directly.
  const sparkColor =
    variant === 'income'
      ? 'var(--amount-positive)'
      : variant === 'expense'
        ? 'var(--amount-negative)'
        : amount < 0
          ? 'var(--amount-negative)'
          : amount > 0
            ? 'var(--amount-positive)'
            : 'var(--amount-neutral)';

  // Secondary line — per-variant
  const perDay = periodDays && periodDays > 0 ? Math.abs(amount) / periodDays : null;
  let secondary: string | null = null;
  if (variant === 'net') {
    if (incomeForRate && incomeForRate > 0) {
      const rate = Math.round((amount / incomeForRate) * 100);
      secondary = rate >= 0 ? `${rate}% poupado` : `${Math.abs(rate)}% déficit`;
    } else if (perDay !== null) {
      secondary = `${perDayFormatter.format(perDay)}/dia`;
    }
  } else if (perDay !== null) {
    secondary = `${perDayFormatter.format(perDay)}/dia`;
  }

  return (
    <div className={cn('flex min-w-0 flex-col gap-2 rounded-[var(--radius-2xl)] border border-border bg-surface p-4 shadow-[var(--shadow-1)] sm:min-h-[180px] sm:px-5 sm:py-[18px]', className)}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className={cn('size-1.5 shrink-0 rounded-full', cfg.dotClass)} aria-hidden />
          <span className="truncate font-mono text-2xs font-semibold tracking-[0.1em] text-text-tertiary uppercase">
            {cfg.label}
          </span>
        </div>
        {typeof count === 'number' && count > 0 && (
          <span className="shrink-0 font-mono text-2xs text-text-tertiary tabular-nums">
            {count}
          </span>
        )}
      </div>

      <div className="flex min-w-0 items-center gap-1.5">
        {Icon && <Icon className={cn('size-4 shrink-0', accent)} weight="bold" />}
        <p
          className={cn('min-w-0 truncate font-mono text-sm leading-none font-medium tabular-nums sm:text-[1.375rem]', accent)}
          title={fmt(displayed)}
        >
          <MoneyValue text={fmt(displayed)} />
        </p>
      </div>

      {secondary && (
        <p className="truncate font-mono text-2xs font-medium text-text-tertiary tabular-nums">
          {secondary}
        </p>
      )}

      {/* Sparkline is desktop-only: on mobile the cards sit two-up (Entradas/Saídas) and must stay
          compact, so the trend chart is dropped there and the number carries the summary. */}
      {series && series.length >= 2 && (
        <div className="mt-auto hidden pt-1 sm:block">
          <Sparkline values={series} color={sparkColor} gradientId={`spark-${variant}`} />
        </div>
      )}
    </div>
  );
}
